import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Filters,
  Page,
  PageHeader,
  Table,
  Toolbar,
} from '@evogard/ui';
import {
  count,
  csvCell,
  csvContent,
  csvDecimal,
  formatInstant,
  loadFailureMessage,
} from '@evogard/domain';
import type {
  Commission,
  PeriodCommissions,
  Period,
  CommissionBreakdown,
  CommissionExport,
  ReasonNotToSettle,
  SettlementResult,
} from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { saveCsv } from '../../lib/csv.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  PeriodField,
  FieldSlot,
  REASON,
  PAYMENT_UNKNOWN,
  WhyNotSettled,
  PaymentBadge,
  MoneyValue,
  currentPeriod,
  periodLabel,
} from './common.js';

/**
 * A comissão apurada de uma competência.
 *
 * É a tela que mostra **quanto uma pessoa vai receber**, e por isso ela é a que
 * mais recusa a mostrar coisa. Três recusas, e cada uma existe por um defeito
 * medido no que roda hoje:
 *
 * ## 1. Sem apuração não há R$ 0,00
 *
 * `sem_apuracao` quer dizer que esta base nunca apurou comissão, em competência
 * nenhuma — e não que março deu zero. Um zero aqui diria a um time inteiro que
 * ele não ganhou nada no mês; a diferença entre as duas frases é a tela toda.
 *
 * ## 2. A comissão NÃO é tudo o que a pessoa recebe
 *
 * Comissão e bonificação são coisas distintas, e o consultor recebe as duas. No
 * sistema antigo, a rota de bonificações devolve um número menor de propósito e
 * o retorno não sinaliza isso: o mesmo consultor, no mesmo mês, vale R$ 0,00 na
 * tela do administrador, R$ 700,00 no próprio perfil e R$ 950,00 na visão do
 * super administrador — e ele vê o do meio.
 *
 * Aqui a bonificação viaja em cada linha justamente para a tela **não poder**
 * ler a comissão como "o que a pessoa ganha". Hoje ela só sabe dizer que não
 * sabe, e a tela repete isso ao lado do total: o número abaixo é a comissão, e
 * não a remuneração.
 *
 * ## 3. "Não sabemos" nunca vira "não pago"
 *
 * A plataforma não guarda pagamento de comissão — a verdade sobre isso é o
 * ciclo de pagamento da gestão, que ainda não existe. Dizer a alguém que a
 * comissão dele não foi paga quando a casa apenas não sabe é a mentira mais cara
 * que este módulo pode contar, e é a razão de `pagamento` ser um estado próprio
 * em vez de uma data vazia.
 *
 * ## Apurar reescreve, e o aviso vem antes do botão
 *
 * Reapurar uma competência substitui a linha de cada consultor daquele mês: a
 * chave (base, consultor, competência) é o que torna a apuração idempotente, e o
 * preço é que o número anterior deixa de existir. A proteção "comissão paga
 * nunca é reescrita" depende de existir quem marque o pagamento — e ninguém
 * marca. Quem apura hoje apura sem essa rede, e a tela diz isso com essas
 * palavras.
 */
export function CommissionsScreen() {
  const { data: context } = useSessionContext();
  const canAssess = can(context, 'sales.commissions', 'apurar');
  const canExportar = can(context, 'sales.commissions', 'exportar');

  const [period, setPeriod] = useState<Period>(currentPeriod());
  const tenantId = context?.currentTenant.id;

  const commissionsQuery = useQuery({
    queryKey: ['comercial', 'comissoes', tenantId, period],
    queryFn: async () => {
      const r = await api.sales.listCommissions({ query: { period } });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'as comissões desta competência'));
      }
      return r.body;
    },
    /*
     * Segura o resultado anterior só dentro da mesma base. Aqui isso não é
     * conforto de interface: as comissões da CE sob o nome da GO seriam a tela
     * afirmando quanto pessoas de outra praça vão receber.
     */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const commissions = commissionsQuery.data;

  return (
    <Page measure="cheia">
      <PageHeader title="Comissões">
        O que a competência paga a cada consultor de{' '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>.
        Uma linha por consultor e mês — a comissão não é decomponível por venda.
      </PageHeader>

      {/*
       * A barra de `@evogard/ui`, e não a de uma tabela: o recorte governa o
       * cartão de apurar, a lista de comissões e a régua por pessoa — prendê-lo
       * a uma das tabelas faria o filtro de uma mudar as outras.
       *
       * Sem campo de busca: esta tela não tem recorte de texto nenhum, e um
       * campo de pesquisa que não pesquisa nada é a barra prometendo o que a
       * rota não faz.
       */}
      <Toolbar
        toolbar={
          <Filters
            values={{ period }}
            onApply={(values) =>
              setPeriod((String(values.period ?? '') || currentPeriod()) as Period)
            }
            fields={[
              {
                type: 'periodo',
                name: 'period',
                label: 'Competência',
                /*
                 * A competência é obrigatória na rota, e o painel aplica o vazio
                 * no "Limpar" como em toda tela da casa — então o vazio precisa
                 * ter significado declarado em vez de ser recusado em silêncio.
                 * Ele devolve o recorte ao mês em que a tela nasce.
                 */
                help: 'Obrigatória. Vazio volta ao mês de hoje.',
              },
            ]}
          />
        }
      />

      {canAssess && <Assessment period={period} />}

      {commissionsQuery.isError && (
        <Alert tone="erro">{commissionsQuery.error.message}</Alert>
      )}

      {commissionsQuery.isPending && !commissionsQuery.isError && (
        <Card title="Carregando…">
          <span className="esqueleto block h-4 w-2/3" />
          <span className="esqueleto mt-2 block h-4 w-1/2" />
        </Card>
      )}

      {commissions?.estado === 'sem_apuracao' && (
        <WhyNotSettled
          title="Esta base nunca apurou comissão."
          reasons={commissions.reasons}
        >
          <p className="mt-1">
            Não há linha de comissão nesta base em competência nenhuma — nem nesta, nem
            nas anteriores. Isso não quer dizer que a competência deu R$ 0,00: quer dizer
            que a conta nunca foi feita, e os motivos abaixo dizem o que falta para
            fazê-la.
          </p>
        </WhyNotSettled>
      )}

      {commissions?.estado === 'apurada' && <PeriodAssessed commissions={commissions} />}

      {canExportar && <Extract periodInitial={period} />}
    </Page>
  );
}

/* ------------------------------------------------------------------------- *
 * A competência apurada
 * ------------------------------------------------------------------------- */

function PeriodAssessed({
  commissions,
}: {
  commissions: Extract<PeriodCommissions, { estado: 'apurada' }>;
}) {
  const bonusReasons = bonusunirReasons(commissions.rows);

  return (
    <div className="flex flex-col gap-5">
      <Card
        title={`Custo de ${periodLabel(commissions.period)}`}
        descricao="A soma das linhas abaixo, feita no servidor — a tela não soma dinheiro."
      >
        <p className="text-xl font-bold">
          <MoneyValue value={commissions.total} />
        </p>
        <p className="mt-1 text-sm text-texto-suave">
          {count(commissions.rows.length, 'consultor', 'consultores')} com linha nesta
          competência.{' '}
          <strong className="font-medium text-texto">
            Este total é de comissão, e não da remuneração das pessoas
          </strong>{' '}
          — a bonificação é outra conta, e a casa ainda não a apura.
        </p>
      </Card>

      {/*
       * Um cartão só para a bonificação e o pagamento, acima da tabela: as duas
       * frases valem para todas as linhas, e repeti-las em cada uma gastaria a
       * atenção que elas precisam comprar. A união dos motivos, e não os da
       * primeira linha, para o cartão continuar dizendo a verdade sobre as
       * outras no dia em que uma delas mudar.
       */}
      <WhyNotSettled title="O que este número NÃO diz." reasons={bonusReasons}>
        <p className="mt-1">
          <strong className="font-medium">A bonificação não está aqui.</strong> Ela é um
          conjunto de prêmios por ritmo e por disputa, distinto da comissão, e o consultor
          recebe as duas. Somar só a comissão e apresentá-la como o que a pessoa ganha é o
          defeito que o sistema antigo comete hoje — lá, o mesmo consultor vale R$ 0,00,
          R$ 700,00 e R$ 950,00 em três telas, e vê o do meio.
        </p>
        <p className="mt-2">
          <strong className="font-medium">E nada aqui fala de pagamento.</strong>{' '}
          {PAYMENT_UNKNOWN}
        </p>
      </WhyNotSettled>

      <Card
        title="Comissão por consultor"
        descricao="Uma linha por pessoa. O valor é da competência inteira, e não de uma venda."
        bleed
      >
        <Table
          label="Comissão por consultor"
          items={[...commissions.rows]}
          rowKey={(l) => String(l.consultant.id)}
          /*
           * Vazio DENTRO de `apurada` é uma resposta sobre o mês — foi apurado e
           * ninguém alcançou faixa —, e não a ausência de apuração. São as duas
           * leituras que a união do contrato existe para separar, e a frase
           * precisa deixar claro qual das duas é esta.
           */
          empty="Esta competência foi apurada e nenhum consultor recebeu comissão nela. É uma resposta sobre o mês: a conta foi feita, e ninguém alcançou faixa."
          columns={[
            {
              title: 'Consultor',
              alignment: 'inicio' as const,
              minWidth: '14rem',
              cell: (l) => (
                <div className="min-w-0">
                  <p className="truncate">
                    {l.consultant.user.nome}
                    {l.archivedAt && (
                      <Badge tone="neutro" className="ml-2">
                        Arquivada
                      </Badge>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs tabular-nums text-texto-suave">
                    {/* o código é POR BASE — o mesmo operador é 394 no CE e 75 no
                        GO —, e quem confere um número contra o ERP precisa saber
                        com qual código conferir */}
                    código {l.consultant.erpVolunteerCode}
                  </p>
                </div>
              ),
            },
            {
              title: 'Comissão',
              alignment: 'fim',
              minWidth: '8rem',
              cell: (l) => (
                <span className="font-medium">
                  <MoneyValue value={l.value} />
                </span>
              ),
            },
            {
              title: 'De onde saiu',
              /* a recomposição são quatro linhas curtas e uma conta de duas
                 parcelas; abaixo disto a conta quebra e deixa de parecer conta */
              minWidth: '20rem',
              cell: (l) => <Breakdown commission={l} />,
            },
            {
              title: 'Bonificação',
              cell: (l) =>
                l.bonus.estado === 'nao_apurada' ? (
                  <Badge tone="informacao">Não apurada</Badge>
                ) : null,
            },
            {
              title: 'Pagamento',
              minWidth: '13rem',
              cell: (l) => <PaymentBadge payment={l.payment} />,
            },
          ]}
        />
      </Card>
    </div>
  );
}

/** Os motivos da bonificação de todas as linhas, sem repetir. */
function bonusunirReasons(rows: readonly Commission[]): ReasonNotToSettle[] {
  const seen = new Set<ReasonNotToSettle>();
  for (const l of rows) {
    if (l.bonus.estado === 'nao_apurada') {
      for (const m of l.bonus.reasons) seen.add(m);
    }
  }
  return [...seen];
}

/**
 * De onde saiu o valor — a conta inteira, sem refazer nenhuma.
 *
 * ## Por que esta coluna existe
 *
 * A tela "Comissões" do sistema antigo mostra, para o mesmo consultor no mesmo
 * mês, **trinta e sete linhas, trinta e três placas e R$ 345,00** — e nenhum dos
 * três é derivável dos outros dois olhando a tela (§5.1 do levantamento). Quem
 * recebe não consegue conferir o próprio pagamento. É esse defeito que esta
 * coluna existe para não repetir: mostrar o total e esconder a conta seria a
 * mesma tela com outra cara.
 *
 * ## A tela não calcula nada, e é por isso que ela fecha
 *
 * Não há uma soma, uma subtração nem uma divisão escritas aqui. Os seis números
 * chegam prontos, e a recomposição — `valor = valorDasPlacas + valorDoBronze` —
 * é garantida pelo **banco**, no `sales_commissions_recomposition_ck`, na hora
 * de gravar. Uma linha em que o total e a explicação discordam não chega a
 * existir, então a tela não precisa conferi-la; se conferisse, teria uma segunda
 * versão da conta para divergir da primeira.
 *
 * Pelo mesmo motivo a tela **não escreve "20 × R$ 10,00"**: o número de placas
 * que a faixa paga é `countedPlates` menos `bronzePlates`, e fazer essa
 * subtração aqui seria a tela produzindo um número que ninguém gravou. Ela diz
 * *quais* placas cada parcela pagou e mostra o que cada uma pagou.
 *
 * ## O caso B, que é o que confunde todo mundo
 *
 * Cinco placas Bronze valem **R$ 275,00**, e não R$ 75,00: elas contam para
 * escolher a faixa das outras **e** pagam o valor fixo por fora dela. Quem ler
 * "Bronze paga R$ 15" e multiplicar por cinco erra R$ 200,00, para menos — o
 * lado do qual ninguém reclama. Por isso a dupla contagem do Bronze é dita com
 * essas palavras sempre que houver Bronze, e não deixada para o leitor deduzir
 * de dois números postos lado a lado.
 */
function Breakdown({ commission }: { commission: Commission }) {
  const breakdown = commission.breakdown;

  return (
    <div className="min-w-0 text-xs text-texto-suave">
      {/*
       * Ternário e não `if` com `return null`: a versão anterior saía por
       * `return null` no ramo que não conhecia, e o contrato registrou que isso
       * faria o estado novo chegar e não aparecer. Assim, o terceiro estado — se
       * nascer — não casa com `HowTheValueIsReached` e para a compilação.
       */}
      {breakdown.estado === 'nao_gravado' ? (
        <WithoutBreakdown breakdown={breakdown} />
      ) : (
        <HowTheValueIsReached breakdown={breakdown} />
      )}

      {/*
       * O instante fica fora dos dois ramos porque vale para os dois: ele é o que
       * separa "a comissão de março" de "o que a tela calcularia sobre março
       * hoje", e é a resposta ao defeito do legado, que recalcula no navegador a
       * cada abertura.
       */}
      <p className="mt-1.5">
        Apurada em {formatInstant(commission.assessedAt)}: é o que a régua daquela
        competência produziu naquele instante, e não o que a tela calcularia hoje.
      </p>
    </div>
  );
}

/**
 * A conta, quando ela chega.
 *
 * A ordem das linhas é a ordem da própria regra, e não a das colunas do banco:
 * primeiro **quantas placas contaram** — porque é esse número que escolhe a
 * faixa —, depois **qual faixa saiu dele**, depois **o que cada parcela pagou**,
 * e por último o que ficou de fora. Lida de cima para baixo, ela responde "por
 * que este valor?" sem voltar.
 */
function HowTheValueIsReached({
  breakdown,
}: {
  breakdown: Extract<CommissionBreakdown, { estado: 'gravado' }>;
}) {
  const { tier, platesCounted, platesBronze, platesIgnoradas } = breakdown;

  return (
    <div>
      <p>
        <strong className="font-medium text-texto">
          {count(platesCounted, 'placa contou', 'placas contaram')}
        </strong>{' '}
        nesta competência — é este número que escolheu a faixa.
        {platesBronze > 0 && (
          <> Dessas, {count(platesBronze, 'é', 'são')} do Plano Bronze.</>
        )}
      </p>

      {tier ? (
        <p className="mt-1">
          <span className="rotulo">faixa aplicada</span> a partir de{' '}
          {count(tier.platesMin, 'placa', 'placas')} no mês,{' '}
          <MoneyValue value={tier.amountPerPlate} /> por placa.
        </p>
      ) : (
        /*
         * Faixa nula é estado NORMAL — "o total não alcançou piso nenhum" —, e
         * não detalhamento ausente. A frase precisa dizer as duas coisas de uma
         * vez, porque o total ao lado pode ser maior que zero mesmo assim: o
         * Bronze paga por fora da faixa.
         */
        <p className="mt-1">
          <span className="rotulo">sem faixa</span> o total não alcançou o menor piso da
          régua desta competência. As placas comuns não pagam nada aí — as do Plano
          Bronze, sim, porque elas pagam por fora da faixa.
        </p>
      )}

      <dl className="mt-1.5 grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5">
        {/*
         * O rótulo acompanha a faixa. Sem faixa, "pelas placas que a faixa paga"
         * ao lado de R$ 0,00 falaria de uma faixa que não existe — e o zero
         * pareceria uma faixa que paga zero, que é a leitura que o contrato
         * separa em dois estados.
         */}
        <dt>
          {tier ? 'pelas placas que a faixa paga' : 'pelas placas comuns, sem faixa'}
        </dt>
        <dd className="text-right text-texto">
          <MoneyValue value={breakdown.platesValue} />
        </dd>
        <dt>pelo Plano Bronze, por fora da faixa</dt>
        <dd className="text-right text-texto">
          <MoneyValue value={breakdown.bronzeValue} />
        </dd>
      </dl>

      <p className="mt-1.5">
        As duas parcelas somam o valor ao lado. Quem soma é o servidor, e o banco recusa
        gravar a linha em que a soma não fecha — a tela não a refaz.
      </p>

      {platesBronze > 0 && (
        /*
         * O caso B do levantamento, dito na tela. Sem esta frase, os dois números
         * do Bronze (quantas são e quanto pagaram) ficam lado a lado convidando à
         * leitura errada — cinco Bronze valem R$ 275,00 e não R$ 75,00 —, e é
         * justamente a dúvida que o sistema antigo gera.
         */
        <p className="mt-1.5">
          <strong className="font-medium text-texto">
            A placa do Plano Bronze conta duas vezes.
          </strong>{' '}
          {tier ? (
            <>
              Ela entra nas placas contadas acima, que escolheram a faixa,{' '}
              <strong className="font-medium text-texto">e</strong> paga o valor fixo dela
              por fora dessa faixa. Sem as do Bronze o total contado seria menor, e podia
              cair para uma faixa mais baixa — ou para nenhuma.
            </>
          ) : (
            /*
             * Sem faixa, dizer que o Bronze "escolheu a faixa das outras" seria
             * falso: não há faixa escolhida. O que continua verdadeiro, e é o que
             * explica o valor ao lado, é a outra metade da regra — o Bronze paga
             * mesmo assim.
             */
            <>
              Ela conta para o total que escolheria a faixa — aqui esse total não alcançou
              piso nenhum — <strong className="font-medium text-texto">e</strong> paga o
              valor fixo dela mesmo assim. É por isso que há valor ao lado sem faixa
              nenhuma.
            </>
          )}
        </p>
      )}

      <p className="mt-1.5">
        {platesIgnoradas > 0 ? (
          <>
            <strong className="font-medium text-texto">
              {count(platesIgnoradas, 'placa ficou', 'placas ficaram')} de fora
            </strong>{' '}
            desta régua: não contaram para a faixa e não pagaram nada. É a diferença entre
            o que a pessoa cadastrou no mês e o que a comissão pagou.
          </>
        ) : (
          <>Nenhuma placa do mês ficou de fora desta régua.</>
        )}
      </p>
    </div>
  );
}

/**
 * O que a tela diz quando a conta não chega.
 *
 * A frase é sobre **a resposta**, e não sobre o banco: esta tela não sabe o que
 * está gravado, sabe o que chegou. Dizer "o detalhe não foi gravado" seria
 * afirmar algo sobre o outro lado que o que chegou aqui não prova — e hoje seria
 * falso, porque as seis colunas são `not null` e nenhuma linha está sem detalhe:
 * o que falta é a leitura projetá-las.
 *
 * E o que falta é dito por inteiro, porque meio detalhamento é pior que nenhum:
 * sem a contagem do Plano Bronze — que paga um valor fixo **por fora** da faixa
 * e ainda assim conta para escolhê-la — o valor não se recompõe, e a explicação
 * exibida não fecharia com o número ao lado.
 */
function WithoutBreakdown({
  breakdown,
}: {
  breakdown: Extract<CommissionBreakdown, { estado: 'nao_gravado' }>;
}) {
  return (
    <div>
      <p>
        Esta resposta não traz a origem do valor — quantas placas contaram, quantas eram
        do Plano Bronze, qual faixa pagou e quanto cada parcela pagou.
      </p>
      {/*
       * Os motivos são mostrados quando vêm. Hoje a lista chega vazia, e o
       * ramo existe porque ignorá-la faria a tela engolir em silêncio a
       * explicação no dia em que o servidor passasse a mandá-la — que é o
       * jeito mais barato de um campo novo nunca chegar a ninguém.
       */}
      {breakdown.reasons.length > 0 && (
        <ul className="mt-1 flex flex-col gap-0.5">
          {breakdown.reasons.map((m) => (
            <li key={m}>{REASON[m].title}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Apurar
 * ------------------------------------------------------------------------- */

/**
 * Mandar apurar a competência.
 *
 * O resultado tem dois desfechos e os dois aparecem: `nao_apurada` traz os
 * motivos e **nada é gravado**; `apurada` traz o resumo — quantos consultores
 * ganharam linha e quanto a competência custa.
 *
 * Um "apurei" com zero linhas seria a pior resposta possível aqui: quem clicou
 * leria "apurei, e não havia nada", quando o que aconteceu foi a casa não saber
 * calcular. Por isso o desfecho negativo é uma resposta inteira, com nome.
 *
 * ## O consultor sem régua declarada deixou de existir, em 22/10/2026
 *
 * Ele foi um **400** até 29/08 e o motivo `consultor_sem_perfil` até 22/10,
 * quando a régua virou obrigatória no cadastro. As ausências que sobram chegam
 * juntas pelo mesmo `WhyNotSettled`.
 *
 * O `Alert` de erro **continua**, e não é código morto: o 500, o 403 e a rede
 * caída passam por ele. O que saiu dali foi uma recusa que nunca foi erro.
 *
 * A guarda é sobre a **pessoa**, e não sobre a placa. A versão anterior dela
 * olhava as placas de reativação, e deixava passar o caso mais silencioso: a
 * consultora da base de reativação que num mês só teve venda comum era paga pela
 * régua do consultor comum, e nada na tela dizia.
 */
function Assessment({ period }: { period: Period }) {
  const client = useQueryClient();
  const [result, setResult] = useState<SettlementResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const assess = useMutation({
    mutationFn: async () => {
      const r = await api.sales.assessCommissions({ body: { period } });
      return ok(r, 200, 'Não foi possível apurar a competência.');
    },
    onSuccess: (body) => {
      setResult(body);
      /* A listagem é quem mostra as linhas — duas rotas produzindo a mesma
         lista é como duas telas passam a discordar. Recarrega em vez de
         desenhar o que o resumo devolveu. */
      void client.invalidateQueries({ queryKey: ['comercial', 'comissoes'] });
    },
    onError: (e: Error) => {
      setResult(null);
      setError(e.message);
    },
  });

  return (
    <Card
      title={`Apurar ${periodLabel(period)}`}
      descricao="Calcula a comissão de cada consultor da base nesta competência e grava."
      actions={
        <Button
          loading={assess.isPending}
          onClick={() => {
            setError(null);
            setResult(null);
            assess.mutate();
          }}
        >
          Apurar
        </Button>
      }
    >
      {error && (
        <Alert tone="erro" title="A competência não foi apurada." className="mt-3">
          {error}
        </Alert>
      )}

      {result?.estado === 'nao_apurada' && (
        <div className="mt-3">
          <WhyNotSettled
            title="A competência não foi apurada, e nada foi gravado."
            reasons={result.reasons}
          />
        </div>
      )}

      {result?.estado === 'apurada' && (
        <Alert
          tone="sucesso"
          title={`Competência apurada em ${formatInstant(result.assessedAt)}.`}
          className="mt-3"
        >
          <p className="mt-1">
            {count(result.consultants, 'consultor', 'consultores')} com linha nesta
            competência, somando <MoneyValue value={result.total} /> de comissão. A lista
            abaixo foi recarregada a partir da leitura — este resumo não a substitui.
          </p>
        </Alert>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------------- *
 * Exportar
 * ------------------------------------------------------------------------- */

/**
 * A exportação de um intervalo de competências.
 *
 * É ação própria porque é outro tamanho de consequência: a listagem responde por
 * **um** mês; isto atravessa um intervalo e leva a remuneração de todo mundo
 * junta, num arquivo que sai da tela.
 *
 * O arquivo é escrito aqui, na borda, porque a API devolve dado estruturado e
 * não um `text/csv`: um blob não passaria pelo Zod, e o contrato não conseguiria
 * dizer que colunas ele tem. O que a tela **não** faz é somar: o total do
 * arquivo é o `total` que o servidor mandou, ao lado das linhas que ele somou.
 */
function Extract({ periodInitial }: { periodInitial: Period }) {
  const { data: context } = useSessionContext();
  const [from, setFrom] = useState<Period>(periodInitial);
  const [until, setUntil] = useState<Period>(periodInitial);
  const [error, setError] = useState<string | null>(null);
  const [last, setLast] = useState<string | null>(null);

  const exportar = useMutation({
    mutationFn: async () => {
      const r = await api.sales.exportCommissions({ query: { de: from, until } });
      return ok(r, 200, 'Não foi possível exportar.');
    },
    onSuccess: (data) => {
      downloadCsv(data, context?.currentTenant.nome ?? 'base');
      setLast(
        `${count(data.rows.length, 'linha', 'linhas')} de ${periodLabel(data.de)} a ${periodLabel(data.until)}, geradas em ${formatInstant(data.generatedAt)}.`,
      );
    },
    onError: (e: Error) => {
      setLast(null);
      setError(e.message);
    },
  });

  return (
    <Card
      title="Exportar comissões"
      descricao="Várias competências de uma vez, da base inteira, num arquivo."
    >
      {/*
       * `items-start`, e o botão dentro de `FieldSlot`: com `items-end` ele
       * alinhava pela borda de baixo da célula do campo, que tem a linha do
       * "Inclusive." embaixo — e ficava um degrau abaixo dos dois campos.
       */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="w-44">
          <PeriodField label="De" value={from} onChange={setFrom} help="Inclusive." />
        </div>
        <div className="w-44">
          <PeriodField label="Até" value={until} onChange={setUntil} help="Inclusive." />
        </div>
        <FieldSlot>
          <Button
            variant="contorno"
            loading={exportar.isPending}
            onClick={() => {
              setError(null);
              setLast(null);
              exportar.mutate();
            }}
          >
            Exportar
          </Button>
        </FieldSlot>
      </div>

      <p className="mt-3 text-xs text-texto-suave">
        O arquivo leva o nome de cada consultor e o que ele recebeu de comissão em cada
        mês do intervalo. Ele não diz nada sobre pagamento — nem que foi pago, nem que não
        foi —, e não inclui bonificação.
      </p>

      {/*
       * A recusa aparece com a mensagem do servidor. Ela é real e é a que roda
       * hoje: a base que nunca apurou é recusada, porque o arquivo sairia
       * afirmando R$ 0,00 num período que ninguém apurou.
       */}
      {error && (
        <Alert tone="erro" title="Nada foi exportado." className="mt-3">
          {error}
        </Alert>
      )}

      {last && (
        <Alert tone="sucesso" title="Arquivo gerado." className="mt-3">
          <p className="mt-1">{last}</p>
        </Alert>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------------- *
 * O arquivo
 * ------------------------------------------------------------------------- */

/**
 * Monta o arquivo e o entrega ao navegador.
 *
 * O separador, o escape das células e o decimal com vírgula moram em
 * `@evogard/domain` desde que a exportação de demandas (`demandas-42`) precisou
 * dos mesmos: eram uma cópia privada aqui, e a segunda cópia é como duas regras
 * de escape divergem. O que ficou desta tela é o que é DESTA tela — quais
 * colunas o arquivo tem e o que cada uma diz.
 *
 * As três linhas de metadado vão no fim, depois de uma linha em branco, e não no
 * começo: um cabeçalho de prosa antes da tabela é o que faz a planilha perder o
 * cabeçalho das colunas. Elas existem porque número em documento vem com data —
 * e a data que importa é a de quem montou o arquivo, já que cada linha carrega a
 * sua própria data de apuração.
 */
function downloadCsv(data: CommissionExport, tenantName: string): void {
  const header = [
    'Competência',
    'Consultor',
    'Código do voluntário na base',
    'Comissão apurada (R$)',
    'Apurada em',
    'Bonificação',
    'Pagamento',
  ];

  const rows = data.rows.map((l) => [
    l.period,
    csvCell(l.consultant.user.nome),
    csvCell(l.consultant.erpVolunteerCode),
    csvDecimal(l.value),
    csvCell(formatInstant(l.assessedAt)),
    /*
     * As duas últimas colunas são texto, e o texto é a frase inteira. Um
     * arquivo que saísse com a coluna "Pagamento" vazia seria lido como "não
     * pago" pela primeira pessoa que o abrisse — que é exatamente a afirmação
     * que esta casa não pode fazer.
     */
    csvCell(
      l.bonus.estado === 'nao_apurada'
        ? 'não apurada — a bonificação não está incluída neste valor'
        : '',
    ),
    csvCell(
      l.payment.estado === 'nao_sabemos'
        ? 'a plataforma não sabe se foi paga — quem responde é o ciclo da gestão'
        : '',
    ),
  ]);

  const content = csvContent([
    header.map(csvCell),
    ...rows,
    [],
    [csvCell('Total do período (R$)'), csvDecimal(data.total)],
    [csvCell('Período'), csvCell(`${data.de} a ${data.until}`)],
    [csvCell('Base'), csvCell(tenantName)],
    [csvCell('Gerado em'), csvCell(formatInstant(data.generatedAt))],
  ]);

  saveCsv(content, `comissoes-${data.de}-a-${data.until}`, tenantName);
}
